import { IAddOpportunityFormModel } from '@user/components/Opportunities/AddOpportunityPage/typings'
import { useField, useFormikContext } from 'formik'
import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  activeTargetPests,
  selectAllTargetPests,
} from '@user/store/targetPests/slice'
import { ILeadFormModel } from '@user/components/Leads/AddLeadForm/typings'

export type TargetPestDataType = {
  label: string
  value: string
}

export function useData({ required, name }) {
  const {
    setFieldValue,
    isSubmitting,
    values: { targetPests },
  } = useFormikContext<IAddOpportunityFormModel | ILeadFormModel>()

  const [{}, { error, touched }] = useField<string[]>({
    name,
    validate: (value) => {
      if (required && (!value || value.length === 0)) {
        return 'Required'
      }
    },
  })

  const showError = Boolean(error && touched)
  const helperText = showError ? error : ''

  const allTargetPests = useSelector(selectAllTargetPests)

  const renderSummary = useCallback(
    (options: TargetPestDataType[], text: string) => {
      if (options.length === 1) {
        return options[0].label
      } else {
        return `${options.length} ${text}`
      }
    },
    [],
  )

  const getOptionLabel = useCallback(
    (option: TargetPestDataType) => option.label,
    [],
  )

  const getId = useCallback((option: TargetPestDataType) => {
    return String(option.value)
  }, [])

  const options = useMemo(
    () =>
      activeTargetPests(allTargetPests).map((p) => ({
        value: p.id,
        label: p.name,
      })),
    [allTargetPests],
  )

  useEffect(() => {
    if (targetPests?.length == 0 && required && options?.length == 1) {
      setFieldValue(name, options[0].value)
    }
  }, [targetPests, required, setFieldValue, options, name])

  const initialOption = useMemo(() => {
    if (targetPests && targetPests?.length > 0) {
      const value = options.filter((x) => targetPests?.includes(x.value))
      if (value) return value
    }
    return []
  }, [options, targetPests])

  return {
    getOptionLabel,
    getId,
    options,
    initialOption,
    isSubmitting,
    showError,
    helperText,
    renderSummary,
  }
}
